import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../../components/AdminLayout/AdminLayout';
import DashboardMainContainer from '../../../components/common/Containers/DashboardMainContainer';
import {
  AdminPageTitle,
  AdminSearchField,
  AdminTableHeader,
  AdminTableBody,
  AdminTableMeta,
  AdminStatusBadge,
  AdminRowAvatar,
  AdminRowAction,
  AdminRowMenuItem,
  ADMIN_INLINE_ROW_ACTIONS_MENU_CLASS,
  AdminRowActionsButton,
  AdminAlertText,
  AdminSupportingText,
  AdminSortableTableHeader,
  AdminIcon,
  AdminListFilterDropdown,
  ADMIN_TOOLBAR_CLASS,
  ADMIN_TABLE_CLASS,
} from '../shared/adminUx';
import ConfigurationListPaginationFooter from '../../../components/AdminDashboard/Configuration/ConfigurationListPaginationFooter';
import useClickOutside from '../../../Hooks/useClickOutside';
import { getCustomersForProfileScenarios } from '../../../data/profileModerationScenarios';
import { CONFIG_LIST_PAGE_SIZE } from '../../../utils/configurationTableUx';

const ACCOUNT_STATUS_OPTIONS = ['Active', 'Inactive', 'Restricted', 'Banned', 'Closed'];
const VERIFICATION_STATUS_OPTIONS = ['Pending', 'Verified'];

const ACCOUNT_STATUS_FILTER_OPTIONS = [
  { value: null, label: 'All' },
  ...ACCOUNT_STATUS_OPTIONS.map((status) => ({ value: status, label: status })),
];

const VERIFICATION_STATUS_FILTER_OPTIONS = [
  { value: null, label: 'All' },
  ...VERIFICATION_STATUS_OPTIONS.map((status) => ({ value: status, label: status })),
];

/** Open flags = New or Active handling cases (Under Review / Open map to Active / New). */
const OPEN_FLAG_STATUSES = new Set(['new', 'active', 'open', 'under review', 'escalated']);

function getOpenFlagsCount(customer) {
  if (typeof customer.openFlagsCount === 'number') return customer.openFlagsCount;
  if (Array.isArray(customer.flags)) {
    return customer.flags.filter((f) => {
      const s = String(f.status || '').toLowerCase().trim();
      return OPEN_FLAG_STATUSES.has(s) && s !== 'decided' && s !== 'closed' && s !== 'resolved';
    }).length;
  }
  return 0;
}

/** Account Status: Active, Inactive, Restricted, Banned, Closed. */
function getAccountStatus(customer) {
  const raw = String(customer.status || customer.accountStatus || '').trim();
  if (!raw) return 'Active';
  const lower = raw.toLowerCase();
  if (lower === 'active') return 'Active';
  if (lower === 'inactive' || lower === 'deleted') return 'Inactive';
  if (lower === 'restricted' || lower === 'under review' || lower === 'suspended') return 'Restricted';
  if (lower === 'banned' || lower === 'blocked') return 'Banned';
  if (lower === 'closed') return 'Closed';
  return raw.charAt(0).toUpperCase() + raw.slice(1);
}

/** Verification Status: Pending or Verified. */
function getVerificationStatus(customer) {
  const raw = String(customer.verificationStatus || '').trim();
  if (!raw) return 'Pending';
  const lower = raw.toLowerCase();
  if (lower === 'verified') return 'Verified';
  if (lower === 'pending') return 'Pending';
  return raw.charAt(0).toUpperCase() + raw.slice(1);
}

/** Location: Suburb, STATE postcode (e.g. Melbourne, VIC 3000). */
function getCustomerLocation(customer) {
  const suburb = customer.location?.suburb || customer.suburb || '';
  const state = customer.region || customer.location?.state || customer.state || '';
  const postcode = customer.location?.postcode || customer.postcode || '';
  if (suburb && state && postcode) return `${suburb}, ${state} ${postcode}`;
  if (suburb && postcode) return `${suburb} ${postcode}`;
  if (suburb && state) return `${suburb}, ${state}`;
  return suburb || state || postcode || '—';
}

const Customers = () => {
  const navigate = useNavigate();

  // Customer ID | Customer | Location | Account Status | Flags | Member Since | Last Activity | Action
  const gridTemplateColumns =
    'minmax(100px, 1fr) minmax(140px, 1.6fr) minmax(150px, 1.5fr) minmax(110px, 1.1fr) minmax(72px, 0.7fr) minmax(110px, 1fr) minmax(150px, 1.4fr) minmax(56px, auto)';

  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filters, setFilters] = useState({
    status: null,
    verificationStatus: null,
  });

  const [statusOpen, setStatusOpen] = useState(false);
  const [verificationOpen, setVerificationOpen] = useState(false);

  const statusRef = useRef(null);
  const verificationRef = useRef(null);

  useClickOutside(statusRef, () => setStatusOpen(false));
  useClickOutside(verificationRef, () => setVerificationOpen(false));

  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalFilteredCount, setTotalFilteredCount] = useState(0);
  const [sortField, setSortField] = useState('lastActive');
  const [sortOrder, setSortOrder] = useState('desc');

  const [customerRowActionsOpen, setCustomerRowActionsOpen] = useState({});
  const customerRowActionsRefs = useRef({});

  const toggleCustomerRowActions = (rowKey) => {
    setCustomerRowActionsOpen((prev) => {
      if (prev[rowKey]) {
        return {};
      }
      return { [rowKey]: true };
    });
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      const isOutsideAll = Object.values(customerRowActionsRefs.current).every(
        (ref) => ref && !ref.contains(event.target)
      );
      if (isOutsideAll) setCustomerRowActionsOpen({});
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const matchesSearchQuery = (customer, searchTerm) => {
    if (!searchTerm || searchTerm.trim().length === 0) {
      return true;
    }

    const term = searchTerm.toLowerCase().trim();

    const fullName = `${customer.firstName || ''} ${customer.lastName || ''}`.toLowerCase();
    const nameMatch =
      fullName.includes(term) ||
      (customer.firstName && customer.firstName.toLowerCase().includes(term)) ||
      (customer.lastName && customer.lastName.toLowerCase().includes(term));

    const emailMatch = customer.email && customer.email.toLowerCase().includes(term);

    const mobileClean = customer.mobile ? customer.mobile.replace(/\D/g, '') : '';
    const searchTermClean = term.replace(/\D/g, '');
    const mobileMatch =
      searchTermClean.length > 0 && mobileClean.includes(searchTermClean);

    const customerIdMatch =
      (customer.customerId && customer.customerId.toLowerCase().includes(term)) ||
      (customer.id && String(customer.id).toLowerCase().includes(term));

    return nameMatch || emailMatch || mobileMatch || customerIdMatch;
  };

  const calculateRelevanceScore = (customer, searchTerm) => {
    if (!searchTerm || searchTerm.trim().length === 0) {
      return 0;
    }

    const term = searchTerm.toLowerCase().trim();
    let score = 0;

    const fullName = `${customer.firstName || ''} ${customer.lastName || ''}`.toLowerCase();
    if (fullName.startsWith(term)) {
      score += 1000;
    } else if (fullName.includes(term)) {
      const position = fullName.indexOf(term);
      score += 500 - position;
    }
    if (customer.firstName && customer.firstName.toLowerCase().startsWith(term)) {
      score += 800;
    } else if (customer.firstName && customer.firstName.toLowerCase().includes(term)) {
      score += 400;
    }
    if (customer.lastName && customer.lastName.toLowerCase().startsWith(term)) {
      score += 800;
    } else if (customer.lastName && customer.lastName.toLowerCase().includes(term)) {
      score += 400;
    }

    if (customer.customerId) {
      const customerIdLower = customer.customerId.toLowerCase();
      if (customerIdLower.startsWith(term)) {
        score += 600;
      } else if (customerIdLower.includes(term)) {
        score += 300;
      }
    }

    if (customer.email) {
      const emailLower = customer.email.toLowerCase();
      if (emailLower.startsWith(term)) {
        score += 200;
      } else if (emailLower.includes(term)) {
        score += 100;
      }
    }

    if (customer.mobile) {
      const mobileClean = customer.mobile.replace(/\D/g, '');
      const searchTermClean = term.replace(/\D/g, '');
      if (searchTermClean.length > 0 && mobileClean.startsWith(searchTermClean)) {
        score += 150;
      } else if (searchTermClean.length > 0 && mobileClean.includes(searchTermClean)) {
        score += 50;
      }
    }

    return score;
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  const generateCustomers = () => getCustomersForProfileScenarios();

  useEffect(() => {
    const fetchCustomers = async () => {
      setLoading(true);
      setError(null);
      try {
        await new Promise((resolve) => setTimeout(resolve, 500));
        const allCustomers = generateCustomers();

        let filteredCustomers = allCustomers.filter((customer) => {
          const matchesSearch = matchesSearchQuery(customer, debouncedSearch);

          const accountStatus = getAccountStatus(customer);
          const matchesStatus =
            filters.status == null || accountStatus === filters.status;

          const verificationStatus = getVerificationStatus(customer);
          const matchesVerification =
            filters.verificationStatus == null ||
            verificationStatus === filters.verificationStatus;

          return matchesSearch && matchesStatus && matchesVerification;
        });

        filteredCustomers.sort((a, b) => {
          if (debouncedSearch && debouncedSearch.trim().length > 0) {
            const aScore = calculateRelevanceScore(a, debouncedSearch);
            const bScore = calculateRelevanceScore(b, debouncedSearch);

            if (bScore !== aScore) {
              return bScore - aScore;
            }
          }

          let aValue;
          let bValue;

          if (sortField === 'openFlags') {
            aValue = getOpenFlagsCount(a);
            bValue = getOpenFlagsCount(b);
          } else if (sortField === 'name') {
            aValue = `${a.firstName || ''} ${a.lastName || ''}`.trim().toLowerCase();
            bValue = `${b.firstName || ''} ${b.lastName || ''}`.trim().toLowerCase();
          } else if (sortField === 'location') {
            aValue = getCustomerLocation(a).toLowerCase();
            bValue = getCustomerLocation(b).toLowerCase();
          } else if (sortField === 'verificationStatus') {
            aValue = getVerificationStatus(a);
            bValue = getVerificationStatus(b);
          } else if (sortField === 'status') {
            aValue = getAccountStatus(a);
            bValue = getAccountStatus(b);
          } else if (sortField === 'lastActive') {
            aValue = a.lastActive ? new Date(a.lastActive).getTime() : 0;
            bValue = b.lastActive ? new Date(b.lastActive).getTime() : 0;
          } else if (sortField === 'joined') {
            aValue = a.joined ? new Date(a.joined).getTime() : 0;
            bValue = b.joined ? new Date(b.joined).getTime() : 0;
          } else {
            aValue = a[sortField];
            bValue = b[sortField];
          }

          if (aValue === null || aValue === undefined) return sortOrder === 'asc' ? 1 : -1;
          if (bValue === null || bValue === undefined) return sortOrder === 'asc' ? -1 : 1;

          if (typeof aValue === 'string' && typeof bValue === 'string') {
            return sortOrder === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
          }
          return sortOrder === 'asc' ? aValue - bValue : bValue - aValue;
        });

        const totalCount = filteredCustomers.length;
        const computedTotalPages = Math.max(1, Math.ceil(totalCount / CONFIG_LIST_PAGE_SIZE));
        const clampedPage = Math.min(currentPage, computedTotalPages);
        const startIndex = (clampedPage - 1) * CONFIG_LIST_PAGE_SIZE;

        setTotalFilteredCount(totalCount);
        setTotalPages(computedTotalPages);
        setCustomers(filteredCustomers.slice(startIndex, startIndex + CONFIG_LIST_PAGE_SIZE));

        if (clampedPage !== currentPage) {
          setCurrentPage(clampedPage);
        }
      } catch (err) {
        setError(err.message || 'Failed to load customers');
        setLoading(false);
      } finally {
        setLoading(false);
      }
    };
    fetchCustomers();
  }, [debouncedSearch, filters, currentPage, sortField, sortOrder]);

  const handleFilterChange = (filterName, value) => {
    if (filterName === 'status') {
      setFilters((prev) => ({ ...prev, status: value }));
    } else if (filterName === 'verificationStatus') {
      setFilters((prev) => ({ ...prev, verificationStatus: value }));
    }
    setCurrentPage(1);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    return (
      date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) +
      ', ' +
      date.toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit', hour12: true })
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const goToCustomerDetail = (customer) => {
    const customerId = customer.customerId || customer.id;
    if (!customerId) return;
    navigate(`/admin/users/customers/${customerId}`, { state: { customer } });
  };

  const effectivePage = Math.min(currentPage, totalPages);
  const rangeStart =
    totalFilteredCount === 0 ? 0 : (effectivePage - 1) * CONFIG_LIST_PAGE_SIZE + 1;
  const rangeEnd = Math.min(effectivePage * CONFIG_LIST_PAGE_SIZE, totalFilteredCount);
  const showPagination = totalFilteredCount > CONFIG_LIST_PAGE_SIZE;

  return (
    <AdminLayout>
      <div>
        <AdminPageTitle>Customers</AdminPageTitle>

        <div className={ADMIN_TOOLBAR_CLASS}>
          <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center lg:justify-between">
            <AdminSearchField
              className="w-full max-w-[26.25rem]"
              type="text"
              placeholder="Customer name, Customer ID, email, mobile"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              aria-label="Search customers"
            />

            <div className="flex flex-wrap gap-3 items-center w-full lg:w-auto lg:justify-end">
              <AdminListFilterDropdown
                label="Account Status"
                value={filters.status}
                options={ACCOUNT_STATUS_FILTER_OPTIONS}
                onChange={(value) => handleFilterChange('status', value)}
                menuRef={statusRef}
                open={statusOpen}
                setOpen={setStatusOpen}
              />
              <AdminListFilterDropdown
                label="Verification Status"
                value={filters.verificationStatus}
                options={VERIFICATION_STATUS_FILTER_OPTIONS}
                onChange={(value) => handleFilterChange('verificationStatus', value)}
                menuRef={verificationRef}
                open={verificationOpen}
                setOpen={setVerificationOpen}
              />
            </div>
          </div>
        </div>

        <DashboardMainContainer className={ADMIN_TABLE_CLASS}>
          {loading ? (
            <div className="space-y-4">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="h-12 bg-white animate-pulse rounded"></div>
              ))}
            </div>
          ) : error ? (
            <div className="text-center py-10">
              <AdminAlertText className="text-alertred mb-4 block">{error}</AdminAlertText>
              <AdminRowAction onClick={() => window.location.reload()}>Retry</AdminRowAction>
            </div>
          ) : customers.length === 0 ? (
            <div className="text-center py-10">
              <AdminSupportingText className="text-mediumgray">
                No customers match your filters.
              </AdminSupportingText>
            </div>
          ) : (
            <>
              <div className="grid py-3 text-offblack border-b border-lightgray px-4 gap-4" style={{ gridTemplateColumns }}>
                <div className="flex items-center">
                  <AdminSortableTableHeader onClick={() => handleSort('customerId')}>
                    Customer ID
                  </AdminSortableTableHeader>
                </div>
                <div className="flex items-center">
                  <AdminSortableTableHeader onClick={() => handleSort('name')}>
                    Customer
                  </AdminSortableTableHeader>
                </div>
                <div className="flex items-center">
                  <AdminSortableTableHeader onClick={() => handleSort('location')}>
                    Location
                  </AdminSortableTableHeader>
                </div>
                <div className="flex items-center">
                  <AdminSortableTableHeader onClick={() => handleSort('status')}>
                    Account Status
                  </AdminSortableTableHeader>
                </div>
                <div className="flex items-center">
                  <AdminSortableTableHeader onClick={() => handleSort('openFlags')}>
                    Flags
                  </AdminSortableTableHeader>
                </div>
                <div className="flex items-center">
                  <AdminSortableTableHeader onClick={() => handleSort('joined')}>
                    Member Since
                  </AdminSortableTableHeader>
                </div>
                <div className="flex items-center">
                  <AdminSortableTableHeader onClick={() => handleSort('lastActive')}>
                    Last Activity
                  </AdminSortableTableHeader>
                </div>
                <div className="flex items-center justify-center">
                  <AdminTableHeader>Action</AdminTableHeader>
                </div>
              </div>

              {customers.map((customer, index) => {
                const customerId = customer.customerId || customer.id;
                const rowKey = String(customer.customerId || customer.id || `row-${index}`);
                const openFlags = getOpenFlagsCount(customer);
                const displayName = `${customer.firstName || ''} ${customer.lastName || ''}`.trim() || '—';
                const accountStatus = getAccountStatus(customer);
                const locationLabel = getCustomerLocation(customer);

                return (
                  <div
                    key={customer.id || index}
                    role="button"
                    tabIndex={0}
                    className="grid py-3 px-4 text-offblack border-b border-lightgray last:border-b-0 gap-4 cursor-pointer"
                    style={{ gridTemplateColumns }}
                    onClick={() => goToCustomerDetail(customer)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        goToCustomerDetail(customer);
                      }
                    }}
                  >
                    <div className="flex items-center">
                      <AdminTableBody className="whitespace-nowrap">{customerId || '—'}</AdminTableBody>
                    </div>

                    <div className="flex items-center gap-2 min-w-0">
                      <AdminRowAvatar
                        src={customer.profilePicture || require('../../../assets/img/User.png')}
                        alt={displayName}
                        onError={(e) => {
                          e.target.src = require('../../../assets/img/User.png');
                        }}
                      />
                      <AdminTableBody emphasis className="truncate" title={displayName}>
                        {displayName}
                      </AdminTableBody>
                    </div>

                    <div className="flex items-center min-w-0">
                      <AdminTableBody className="truncate" title={locationLabel}>
                        {locationLabel}
                      </AdminTableBody>
                    </div>

                    <div className="flex items-center">
                      <AdminStatusBadge status={accountStatus} label={accountStatus} />
                    </div>

                    <div className="flex items-center">
                      <AdminTableBody className="tabular-nums">{openFlags}</AdminTableBody>
                    </div>

                    <div className="flex items-center">
                      <AdminTableMeta className="whitespace-nowrap">
                        {formatDate(customer.joined)}
                      </AdminTableMeta>
                    </div>

                    <div className="flex items-center">
                      <AdminTableMeta className="whitespace-nowrap">
                        {formatDateTime(customer.lastActive)}
                      </AdminTableMeta>
                    </div>

                    <div className="flex items-center justify-center relative" onClick={(e) => e.stopPropagation()}>
                      <div
                        className="relative inline-flex"
                        ref={(el) => {
                          customerRowActionsRefs.current[rowKey] = el;
                        }}
                      >
                        <AdminRowActionsButton
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleCustomerRowActions(rowKey);
                          }}
                          title="Actions"
                          aria-label="Row actions"
                        >
                          <AdminIcon>
                            <i className="fi fi-rs-menu-dots-vertical text-offblack" aria-hidden />
                          </AdminIcon>
                        </AdminRowActionsButton>
                        {customerRowActionsOpen[rowKey] && (
                          <div className={ADMIN_INLINE_ROW_ACTIONS_MENU_CLASS}>
                            <AdminRowMenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                goToCustomerDetail(customer);
                                setCustomerRowActionsOpen((prev) => ({ ...prev, [rowKey]: false }));
                              }}
                            >
                              View
                            </AdminRowMenuItem>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </>
          )}
        </DashboardMainContainer>

        <ConfigurationListPaginationFooter
          show={!loading && !error && showPagination}
          rangeStart={rangeStart}
          rangeEnd={rangeEnd}
          totalCount={totalFilteredCount}
          currentPage={effectivePage}
          totalPages={totalPages}
          onPrev={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
          onNext={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
          goToPage={setCurrentPage}
        />
      </div>
    </AdminLayout>
  );
};

export default Customers;
